import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import UserProgress from '@Pages/ClassRooms/Pages/SingleCourse/Pages/Users/UserProgress';
import { formatDuration } from '@Utils/formatDuration';
import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { rgba } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { StatusChip, SubscriptionStatus } from '../Subscriptions/subscriptions.shared';

type SubscriptionRow = {
	id: number;
	title: string;
	code: string;
	price: number;
	instructor_name: string;
	purchase_method: string | null;
	source_bundle_id?: number | null;
	source_bundle_title?: string | null;
	enrolled_at: Date | null;
	activate_until?: Date | null;
	last_visited?: Date | null;
	status: SubscriptionStatus;
	progress: number;
	time?: number;
	lectures_count?: number;
	started_lectures?: number;
	completed_lectures?: number;
	courses_count?: number;
	enrollment_id?: number | null;
};

export type Overview = {
	type: 'standalone' | 'bundle';
	subscription: SubscriptionRow;
	members?: SubscriptionRow[];
};

type Props = { overview?: Overview; onOpenCourse: (courseId: number) => void };

const Stat: React.FC<{ label: string; tone?: string }> = ({ label, tone = '#5ac0fc', children }) => (
	<StatCard tone={tone}>
		<StatLabel tone={tone}>{label}</StatLabel>
		<StatValue>{children}</StatValue>
	</StatCard>
);

const date = (value?: Date | null) => (value ? <DateTimeFormatter date={value} /> : '-');

/** Member access as seen from this bundle's report. */
const memberAccess = (member: SubscriptionRow, bundleId: number) => {
	if (!member.enrollment_id) return 'Not held';
	if (member.source_bundle_id === bundleId) return 'Via this bundle';
	if (member.source_bundle_id) return `Via bundle: ${member.source_bundle_title ?? member.source_bundle_id}`;
	return 'Owned individually';
};

const PerformanceOverview: React.FC<Props> = ({ overview, onOpenCourse }) => {
	if (!overview) {
		return (
			<Center>
				<CircularProgress size={28} />
			</Center>
		);
	}
	const { subscription: sub } = overview;
	const isBundle = overview.type === 'bundle';

	return (
		<>
			<Grid>
				<Stat label="Status">
					<StatusChip status={sub.status} />
				</Stat>
				<Stat label="Progress" tone="#00d66b">
					<UserProgress progress={Number(sub.progress || 0)} />
				</Stat>
				<Stat label="Learning Time" tone="#6C63FF">
					{Number(sub.time) > 0 ? formatDuration(Number(sub.time)) : '0'}
				</Stat>
				{isBundle ? (
					<Stat label="Courses" tone="#ffab00">
						{sub.courses_count ?? overview.members?.length ?? 0}
					</Stat>
				) : (
					<Stat label="Lectures (completed / started / total)" tone="#ffab00">
						{sub.completed_lectures ?? 0} / {sub.started_lectures ?? 0} / {sub.lectures_count ?? 0}
					</Stat>
				)}
			</Grid>

			<Details>
				<Detail>
					<dt>Instructor</dt>
					<dd>{sub.instructor_name || '-'}</dd>
				</Detail>
				<Detail>
					<dt>Price</dt>
					<dd>{sub.price}</dd>
				</Detail>
				<Detail>
					<dt>Purchase method</dt>
					<dd>{sub.purchase_method || '-'}</dd>
				</Detail>
				<Detail>
					<dt>Enrolled at</dt>
					<dd>{date(sub.enrolled_at)}</dd>
				</Detail>
				{!isBundle && (
					<>
						<Detail>
							<dt>Source</dt>
							<dd>{sub.source_bundle_id ? `Bundle: ${sub.source_bundle_title ?? sub.source_bundle_id}` : 'Individual'}</dd>
						</Detail>
						<Detail>
							<dt>Access until</dt>
							<dd>{sub.activate_until ? date(sub.activate_until) : 'No limit'}</dd>
						</Detail>
						<Detail>
							<dt>Last visited</dt>
							<dd>{date(sub.last_visited)}</dd>
						</Detail>
					</>
				)}
			</Details>

			{isBundle && (
				<>
					<SectionTitle>Member courses</SectionTitle>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Course</TableCell>
								<TableCell>Access</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Access until</TableCell>
								<TableCell>Progress</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{(overview.members ?? []).map((member) => (
								<TableRow
									key={member.id}
									hover={Boolean(member.enrollment_id)}
									sx={{ cursor: member.enrollment_id ? 'pointer' : 'default' }}
									onClick={() => member.enrollment_id && onOpenCourse(member.id)}
								>
									<TableCell>{member.title}</TableCell>
									<TableCell>{memberAccess(member, sub.id)}</TableCell>
									<TableCell>
										<StatusChip status={member.status} />
									</TableCell>
									<TableCell>
										{!member.enrollment_id ? '-' : member.activate_until ? date(member.activate_until) : 'No limit'}
									</TableCell>
									<TableCell>
										{member.enrollment_id ? <UserProgress progress={Number(member.progress || 0)} /> : '-'}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</>
			)}
		</>
	);
};

const Center = styled.div`
	display: flex;
	justify-content: center;
	padding: 3rem;
`;
const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
	gap: 1rem;
	margin-bottom: 2rem;
`;
const StatCard = styled.div<{ tone: string }>`
	background: ${({ tone }) => rgba(tone, 0.1)};
	border-radius: 5px;
	padding: 1rem 1.25rem;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	min-height: 5rem;
`;
const StatLabel = styled.p<{ tone: string }>`
	font-size: 1rem;
	font-weight: bold;
	color: ${({ tone }) => tone};
`;
const StatValue = styled.div`
	font-size: 1.25rem;
	font-weight: 600;
	color: #251b33;
`;
const Details = styled.dl`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
	gap: 1rem 2rem;
	margin-bottom: 2rem;
`;
const Detail = styled.div`
	dt {
		font-size: 0.8rem;
		color: #6e7577;
		margin-bottom: 0.25rem;
	}
	dd {
		font-weight: 500;
		color: #251b33;
	}
`;
const SectionTitle = styled.p`
	font-size: 1rem;
	font-weight: bold;
	color: #251b33;
	margin-bottom: 0.75rem;
`;

export default PerformanceOverview;
